import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LazyMotion, domAnimation } from "framer-motion";
import Index from "./pages/Index.tsx";
import BrandedLoader from "@/components/BrandedLoader";
import { importers, warmAllRoutes } from "@/lib/routePrefetch";

// Defer toaster bundles (radix-toast + sonner ~15 KiB) until after first paint.
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

// Share importer references with React.lazy so the prefetcher and the router
// both hit the same Vite chunk cache — a warmed chunk renders instantly.
const LatestArticles = lazy(importers.latest as () => Promise<{ default: React.ComponentType }>);
const ArticlePage = lazy(importers.article as () => Promise<{ default: React.ComponentType }>);
const AdminPanel = lazy(importers.admin as () => Promise<{ default: React.ComponentType }>);
const CategoryPage = lazy(importers.category as () => Promise<{ default: React.ComponentType }>);
const SearchPage = lazy(importers.search as () => Promise<{ default: React.ComponentType }>);
const ContactPage = lazy(importers.contact as () => Promise<{ default: React.ComponentType }>);
const TermsOfService = lazy(importers.terms as () => Promise<{ default: React.ComponentType }>);
const PrivacyPolicy = lazy(importers.privacy as () => Promise<{ default: React.ComponentType }>);
const NotFound = lazy(importers.notFound as () => Promise<{ default: React.ComponentType }>);

// Retry transient backend blips (schema-cache reloads return 503/404 briefly)
// with exponential backoff so content pages never render empty on a hiccup.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 4,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: false,
    },
  },
});

// Warms route chunks only after the first user interaction so they never
// compete with critical LCP resources on the initial Lighthouse pass.
const RoutePrefetcher = () => {
  const location = useLocation();
  useEffect(() => {
    let cancelWarm: (() => void) | undefined;
    let done = false;
    const warm = () => {
      if (done) return;
      done = true;
      cleanup();
      cancelWarm = warmAllRoutes();
    };
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "scroll", "touchstart"];
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, warm));
      if (idleId) (window as any).cancelIdleCallback?.(idleId);
    };
    events.forEach((e) => window.addEventListener(e, warm, { once: true, passive: true }));
    // Fallback: idle warm after 15s if no interaction
    const idleId = (window as any).requestIdleCallback?.(() => {
      setTimeout(warm, 15000);
    }, { timeout: 20000 });
    return () => {
      cleanup();
      cancelWarm?.();
    };
  }, [location.pathname]);
  return null;
};

const RouteToasters = () => {
  const { pathname } = useLocation();
  const needsClassicToast = pathname === "/contact";
  const needsSonner = pathname === "/periodical-today-admin";
  if (!needsClassicToast && !needsSonner) return null;

  return (
    <Suspense fallback={null}>
      {needsClassicToast && <Toaster />}
      {needsSonner && <Sonner />}
    </Suspense>
  );
};

const App = () => (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <LazyMotion features={domAnimation}>
      <BrowserRouter>
        <RouteToasters />
        <RoutePrefetcher />
        <Suspense fallback={<BrandedLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/latest" element={<LatestArticles />} />
            <Route path="/article/:slug" element={<ArticlePage />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/periodical-today-admin" element={<AdminPanel />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </LazyMotion>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
